import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { DeliveryBike, WoltPaperBag } from '@creditornot/cb-icons';
import { cssThemedValue } from '@creditornot/cb-ingredients';
import { ModalContent, ModalFooter, ModalHeader } from '@creditornot/cb-modal';
import { RadioCard } from '@creditornot/cb-radio';
import { useMutation } from '@tanstack/react-query';
import React, { FormEvent, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { updateActiveDeliverySettings } from 'apis/delivery-settings-api/api';
import { DeliverySettingsType } from 'apis/delivery-settings-api/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { WoltModal } from 'common/components';
import { useExternalAccessRights } from 'common/hooks';
import { typographyCss } from 'common/typography';
import { usePermissions } from 'common/wolt-permissions';
import * as permissionUtils from 'common/wolt-permissions/permissionUtils';

import { telemetryDeliveryTypeModalDataConfirm, useTelemetryDeliveryTypeModalViewTracking } from '../telemetry';

type Props = {
  originView: 'restaurant_details' | 'live_purchases';
  venue: Venue;
  currentDeliveryProvider: DeliverySettingsType;
  onClose: () => void;
  onSuccess: () => void;
};

const SUCCESS_MESSAGES: { [key in DeliverySettingsType]: string } = {
  wolt_delivery: 'venue-details.delivery-settings.success-wolt-delivery',
  self_delivery: 'venue-details.delivery-settings.success-self-delivery',
};

const StyledWoltModal = styled(WoltModal)`
  width: 400px;
  background: ${cssThemedValue('surfaceMain')};
  border-radius: 8px;
`;

const Ingress = styled.p`
  ${typographyCss.Body2}
  color: ${cssThemedValue('textSecondary')};
  display: block;
  margin-block-start: 0;
  margin-block-end: 24px;
`;

const StyledRadioCard = styled(RadioCard)`
  & + & {
    margin-block-start: 8px;
  }
`;

const RightContent = styled.span`
  opacity: 0.88;

  svg {
    height: 40px;
    fill: ${cssThemedValue('textPrimary')};
  }
`;

const StyledAlert = styled(Alert)`
  margin-block-start: 8px;
`;

const StyledFooter = styled(ModalFooter)`
  justify-content: flex-end;
`;

/** Modal control for changing the currently active delivery provider */
const DeliveryProviderModal = ({ originView, venue, currentDeliveryProvider, onClose, onSuccess }: Props) => {
  const [deliveryProvider, setDeliveryProvider] = useState<DeliverySettingsType>(currentDeliveryProvider);
  const intl = useIntl();

  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const canEdit = permissionUtils.hasDeliveryProviderEditPermissions(permissions, externalAccessRights, venue);

  useTelemetryDeliveryTypeModalViewTracking(originView);

  const mutation = useMutation(
    (activeDeliverySettingsType: DeliverySettingsType) =>
      updateActiveDeliverySettings(venue.id, { activeDeliverySettingsType }),
    {
      onSuccess: () => {
        toast.success(<FormattedMessage id={SUCCESS_MESSAGES[deliveryProvider]} />);
        onSuccess();
      },
      onError: () => {
        toast.error(<FormattedMessage id="venue-details.delivery-settings.delivery-type-error" />, {
          duration: Infinity,
        });
        onClose();
      },
    },
  );
  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canEdit) {
      return;
    }
    telemetryDeliveryTypeModalDataConfirm(deliveryProvider);
    await mutation.mutateAsync(deliveryProvider);
  };

  return (
    <StyledWoltModal isOpen onRequestClose={onClose}>
      <form onSubmit={handleSubmit}>
        <ModalHeader onCloseClick={onClose}>
          <FormattedMessage id="venue-details.delivery-settings.delivery-type-heading" />
        </ModalHeader>
        <ModalContent>
          <Ingress>
            <FormattedMessage id="venue-details.delivery-settings.delivery-type-body" />
          </Ingress>
          <StyledRadioCard
            label={intl.formatMessage({ id: 'venue-details.delivery-settings.wolt-delivery' })}
            subContent={intl.formatMessage({ id: 'venue-details.delivery-settings.wolt-delivery-description' })}
            rightContent={
              <RightContent>
                <WoltPaperBag />
              </RightContent>
            }
            checked={deliveryProvider === 'wolt_delivery'}
            onChange={() => setDeliveryProvider('wolt_delivery')}
          />
          <StyledRadioCard
            label={intl.formatMessage({ id: 'venue-details.delivery-settings.self-delivery' })}
            subContent={intl.formatMessage({ id: 'venue-details.delivery-settings.self-delivery-description' })}
            rightContent={
              <RightContent>
                <DeliveryBike />
              </RightContent>
            }
            checked={deliveryProvider === 'self_delivery'}
            onChange={() => setDeliveryProvider('self_delivery')}
          />
          {!canEdit && (
            <StyledAlert variant="info">
              <FormattedMessage id="venue-details.delivery-settings.permissions-alert" />
            </StyledAlert>
          )}
        </ModalContent>
        <StyledFooter>
          <Button
            type="submit"
            size="small"
            variant="blue"
            loading={mutation.isLoading}
            disabled={!canEdit || mutation.isLoading || currentDeliveryProvider === deliveryProvider}
          >
            <FormattedMessage id="state.confirm" />
          </Button>
        </StyledFooter>
      </form>
    </StyledWoltModal>
  );
};

export default DeliveryProviderModal;
